// Questions the chrome asks of a mode's nav tree.

import type { ViewMode } from '../viewMode';

import { ADMIN_NAV } from './admin-nav';
import type { NavSection, NavTree, SectionId } from './nav-tree.types';
import { TEACHER_NAV } from './teacher-nav';

export function navTreeFor(mode: ViewMode): NavTree {
  return mode === 'admin' ? ADMIN_NAV : TEACHER_NAV;
}

export function sectionsOf(tree: NavTree): NavSection[] {
  return tree.entries.filter((e): e is NavSection => e.kind === 'section');
}

/** The routes that place a page in a section: its landing page, its own
 *  links, and any extra pages it claims. Shortcuts into other sections and
 *  unbuilt pages never count, and neither does an unbuilt section. */
function routesOf(section: NavSection): string[] {
  if (!section.ready) return [];
  const links = section.items.filter((i) => !i.shortcut && i.ready !== false).map((i) => i.to);
  return [section.home, ...links, ...(section.alsoMatches ?? [])];
}

/** The section a page belongs to — the one the chrome highlights and opens.
 *  The longest matching route wins, so a page deep under Settings is not
 *  claimed by a shorter route elsewhere. Null for a page in no section. */
export function sectionFromPath(tree: NavTree, pathname: string): SectionId | null {
  let best: { id: SectionId; length: number } | null = null;
  for (const section of sectionsOf(tree)) {
    for (const route of routesOf(section)) {
      if (pathname.startsWith(route) && (!best || route.length > best.length)) {
        best = { id: section.id, length: route.length };
      }
    }
  }
  return best?.id ?? null;
}
